import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../services/api.ts';
import { useConnectivityStatus } from '../hooks/useConnectivityStatus.ts';
import './Login.css';

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [token, setToken] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const { isOnline, isBackendOnline } = useConnectivityStatus();
  const backendAvailable = isOnline && isBackendOnline;

  useEffect(() => {
    // Pegar token da URL se existir
    const tokenFromUrl = searchParams.get('token');
    if (tokenFromUrl) {
      setToken(tokenFromUrl);
    }
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    setError('');
    setSuccess('');

    // Validações
    if (!token) {
      setError('Token é obrigatório');
      return;
    }

    if (password.length < 6) {
      setError('A senha deve ter pelo menos 6 caracteres');
      return;
    }

    if (password !== confirmPassword) {
      setError('As senhas não coincidem');
      return;
    }

    setLoading(true);

    try {
      await api.post('/api/v1/auth/reset-password', {
        token,
        new_password: password
      });
      
      setSuccess('Senha alterada com sucesso! Redirecionando para login...');
      
      // Redirecionar para login após 2 segundos
      setTimeout(() => {
        navigate('/login', { replace: true });
      }, 2000);
    } catch (err: any) {
      console.error('Erro ao resetar senha:', err);
      setError(err.response?.data?.error || 'Erro ao resetar senha. Verifique se o token é válido e não expirou.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <div className="logo-container">
            <img 
              src="/logo.png"
              alt="Prumo" 
              className="login-logo"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                if (target) {
                  target.style.display = 'none';
                }
              }}
            />
            <h1 className="logo-text">Prumo</h1>
          </div>
          <p className="slogan">Redefinir Senha</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form" noValidate>
          {!backendAvailable && (
            <div className="error-message" role="alert" aria-live="polite">
              Conexão com o backend necessária para redefinir senha.
            </div>
          )}
          {error && (
            <div className="error-message" role="alert" aria-live="polite">
              {error}
            </div>
          )}
          
          {success && (
            <div className="success-message" role="alert" aria-live="polite">
              {success}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="token">Token de Recuperação</label>
            <input
              type="text"
              id="token"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              required
              placeholder="Cole o token recebido por email"
              disabled={loading || !!searchParams.get('token') || !backendAvailable}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Nova Senha</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="Mínimo de 6 caracteres"
              minLength={6}
              disabled={loading || !backendAvailable}
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirmar Nova Senha</label>
            <input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              placeholder="Digite a senha novamente"
              minLength={6}
              disabled={loading || !backendAvailable}
            />
          </div>

          <button 
            type="submit" 
            className="login-button"
            disabled={loading || !backendAvailable}
          >
            {loading ? 'Redefinindo...' : 'Redefinir Senha'}
          </button>
        </form>

        <div className="login-footer">
          <p>
            <Link to="/login" className="link">
              Voltar para login
            </Link>
            {' | '}
            <Link to="/forgot-password" className="link">
              Solicitar novo token
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;

